import { ActionFunctionArgs, json, LoaderFunction } from "@remix-run/node";
import { MetaFunction, useLoaderData, useLocation } from "@remix-run/react";
import { dataJasaBangunRumahByDistrictBekasi } from "~/api/data-jasa-bangun-rumah-by-district-bekasi-by-services/dataJasaBangunRumahByDistrictBekasi";

import {
  ADDRESS,
  ADDRESS_CITY,
  AUTHOR,
  BRAND,
  dateModified,
  datePublished,
  DESCRIPTION_JASA_BANGUN,
  KEYWORD_JASA_BANGUN,
  LOGO,
  OG_IMAGE,
  POSTAL_CODE,
  priceValidUntil,
  PROFILE_FACEBOOK,
  PROFILE_IMAGE,
  PROFILE_INSTAGRAM,
  PROFILE_LINKEDIN,
  SITE_URL,
  SLUG_JASA_BANGUN_RUMAH_DI_BEKASI,
  SPAM_WORD_1,
  TWITTER_IMAGE,
  WHATSAPP_MESSAGE,
  WHATSAPP_NUM,
} from "~/assets/profile/profile";
import GlobalBreadcrumb from "~/components/global/GlobalBreadcrumb";
import { HomeSectionHero } from "../home/components/HomeSectionHero";
import { useEffect, useState } from "react";
import {
  InterfaceBreadcrumbItem,
  InterfaceJasaByDistrict,
  InterfaceJasaByDistrictService,
} from "~/types/GlobalInterfaces";
import GlobalMetaScripts from "~/components/global/GlobalMetaScripts";
import { GlobalFormSchema } from "~/scheme/global_form_schema";
import { parseWithZod } from "@conform-to/zod";
import GlobalCardByServices from "~/components/global/GlobalCardByServices";
import ExploreJasaByCitySection from "~/components/global/ExploreJasaByCitySection";
import GlobalCompoVideo from "~/components/global/GlobalCompoVideo";
import GlobalSeoDescriptionContent from "~/components/global/GlobalSeoDescriptionContent";
import GlobalSectionHero from "~/components/global/GlobalSectionHero";
import { Navbar } from "~/components/layouts/Navbar";
import { Footer } from "~/components/layouts/footer/Footer";
interface DistrictData {
  id: number;
  district: string;
  inaTitle: string;
  slug: string;
  price: string;
}

interface LoaderData {
  districtData: DistrictData;
}

export const loader: LoaderFunction = async ({ params }) => {
  const { districtSlug } = params;
  const districtData = dataJasaBangunRumahByDistrictBekasi.find(
    (dataJasaBangunRumahByDistrictBekasi) =>
      dataJasaBangunRumahByDistrictBekasi.slug === districtSlug
  );

  if (!districtData) {
    throw new Response("Not Found", { status: 404 });
  }
  const breadcrumbItems = [
    { label: "Home", path: "/" },
    {
      label: "Jasa Bangun Rumah Di Bekasi",
      path: "/jasa-bangun-rumah-di-bekasi",
    },
    { label: districtData?.inaTitle, path: districtData?.slug },
  ];

  const dynamicUrls = dataJasaBangunRumahByDistrictBekasi.map(
    (jasa_bangun_rumah: { district: any; slug: any }) => ({
      label: `Jasa Bangun Rumah ${jasa_bangun_rumah.district}`,
      path: `/jasa-bangun-rumah-di-bekasi/${jasa_bangun_rumah.slug}`,
    })
  );

  return json({
    districtData,
    dataJasaBangunRumahByDistrictBekasi: dataJasaBangunRumahByDistrictBekasi,
    dynamicUrls,
    breadcrumbItems,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: BRAND,
      url: SITE_URL,
      logo: LOGO,
      sameAs: [PROFILE_FACEBOOK, PROFILE_INSTAGRAM, PROFILE_LINKEDIN],
    },
    // BUSINESSCHEMA
    businessSchema: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: BRAND,
      image: LOGO,
      address: {
        "@type": "PostalAddress",
        streetAddress: ADDRESS,
        addressLocality: ADDRESS_CITY,
        postalCode: POSTAL_CODE,
        addressCountry: "ID",
      },
      telephone: WHATSAPP_NUM,
      priceRange: "Rp300.000 - Rp10.000.000",
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "09:00",
          closes: "18:00",
        },
      ],
    },
    // SERVICECHEMA
    serviceSchema: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: SPAM_WORD_1,
      provider: {
        "@type": "Organization",
        name: AUTHOR,
        url: SITE_URL,
      },
    },
    // PRODUCTSCHEMA
    productSchema: {
      "@context": "https://schema.org",
      "@type": "Product",
      name: KEYWORD_JASA_BANGUN,
      description: `${DESCRIPTION_JASA_BANGUN}${districtData.inaTitle}`,
      brand: {
        "@type": "Brand",
        name: AUTHOR,
      },
      sku: "GR-001",
      gtin: "123456789012",
      image: PROFILE_IMAGE,
      offers: {
        "@type": "Offer",
        price: "2000000",
        priceCurrency: "IDR",
        availability: "https://schema.org/InStock",
        url: `${SITE_URL}/${districtSlug}`,
        priceValidUntil: priceValidUntil,
        shippingDetails: {
          "@type": "OfferShippingDetails",
          shippingRate: {
            "@type": "MonetaryAmount",
            value: "10000",
            currency: "IDR",
          },
          shippingDestination: {
            "@type": "DefinedRegion",
            addressCountry: "ID",
          },
          deliveryTime: {
            "@type": "ShippingDeliveryTime",
            handlingTime: {
              "@type": "QuantitativeValue",
              minValue: 1,
              maxValue: 2,
              unitCode: "DAY",
            },
            transitTime: {
              "@type": "QuantitativeValue",
              minValue: 2,
              maxValue: 4,
              unitCode: "DAY",
            },
          },
        },
        hasMerchantReturnPolicy: {
          "@type": "MerchantReturnPolicy",
          applicableCountry: "ID",
          returnPolicyCategory:
            "https://schema.org/MerchantReturnFiniteReturnWindow",
          merchantReturnDays: 7,
          returnMethod: "https://schema.org/ReturnByMail",
          returnFees: "https://schema.org/FreeReturn",
        },
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.8",
        reviewCount: "2248",
      },
    },
  });
};
export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const { payload } = parseWithZod(formData, { schema: GlobalFormSchema });

  formData.append("Date", new Date().toISOString());
  formData.append("Whatsapp", String(payload?.whatsapp));
  return json({ msg: "success" });
};
export const meta: MetaFunction = ({ data }) => {
  const districtData = (data as LoaderData).districtData;
  const title =
    `⭐️ NO 1 Ahlinya ${districtData?.inaTitle} | WA ${WHATSAPP_NUM}` ||
    `⭐️ NO 1 Ahlinya | WA ${WHATSAPP_NUM} | Jasa Bangun Rumah`;
  const description = `⭐️ Gratis Konsultasi ! Jasa Bangun Rumah terpercaya di ${districtData.district}? Tukang profesional, hasil rapi & cepat, harga terjangkau. Hubungi sekarang!`;
  const slug = `${SITE_URL}/${SLUG_JASA_BANGUN_RUMAH_DI_BEKASI}/${districtData.slug}`;
  const keywords = [
    "Jasa Bangun Rumah",
    districtData.district,
    "Jasa Renovasi Rumah",
    "Jasa Tukang Bangunan Profesional",
    "Jasa Tukang Berpengalaman",
    "Jasa Pembangunan Rumah Terpercaya",
    "Jasa Tukang Harian",
    "Jasa Pekerjaan Rumah",
    "Tukang Bangunan Bekasi",
    "Jasa Tukang Listrik",
    "Jasa Pasang Keramik",
    "Jasa Perbaikan Rumah",
  ].join(", ");

  const rating = "5.0";
  const author = AUTHOR; // Author of the page
  const publisher = AUTHOR; // Publisher of the page

  return [
    { title: `${title} | arsinesia.com` },
    {
      name: "description",
      content: description,
    },
    {
      name: "keywords",
      content: keywords,
    },
    {
      property: "og:title",
      content: title,
    },
    {
      property: "og:description",
      content: description,
    },
    { property: "og:type", content: "website" },
    { property: "og:url", content: `${slug}` },
    { property: "og:image", content: OG_IMAGE },
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: title,
    },
    {
      name: "twitter:description",
      content: description,
    },
    { name: "twitter:image", content: TWITTER_IMAGE },
    { name: "viewport", content: "width=device-width, initial-scale=1.0" },
    // Additional SEO Meta Tags
    { name: "rating", content: rating },
    { name: "author", content: author },
    { name: "publisher", content: publisher },
    { name: "datePublished", content: datePublished },
    { name: "dateModified", content: dateModified },
    { name: "robots", content: "index, follow" }, // Allows search engines to index the page
    {
      name: "robots",
      content: "max-snippet:-1, max-image-preview:large, max-video-preview:-1", // Controls the preview type
    },
    {
      property: "og:rating",
      content: rating, // Added rating for OG meta
    },
    {
      property: "og:author",
      content: author, // Added author for OG meta
    },
    {
      property: "og:publisher",
      content: publisher, // Added publisher for OG meta
    },
    {
      property: "og:datePublished",
      content: datePublished, // Added publish date for OG meta
    },
    {
      property: "og:dateModified",
      content: dateModified, // Added modify date for OG meta
    },
    {
      property: "schema.org/Organization", // Structured data (JSON-LD)
      content: JSON.stringify({
        "@context": "http://schema.org",
        "@type": "Organization",
        name: publisher,
        url: SITE_URL,
        logo: LOGO, // Replace with your logo URL
        sameAs: PROFILE_FACEBOOK, // Replace with social media URLs
      }),
    },
  ];
};

export default function Index() {
  const {
    districtData,
    breadcrumbItems,
    structuredData,
    serviceSchema,
    businessSchema,
    productSchema,
  } = useLoaderData<{
    districtData: InterfaceJasaByDistrict;
    dataJasaBangunRumahByDistrictBekasiByServices: InterfaceJasaByDistrictService[];
    dynamicUrls: { label: string; path: string }[];

    breadcrumbItems: InterfaceBreadcrumbItem[];
    structuredData: any;
    serviceSchema: any;
    businessSchema: any;
    productSchema: any;
  }>();

  // 🔴🔴🔴 CURRENT PAGE
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState<string | undefined>();
  useEffect(() => {
    const fullPage = `${SITE_URL}${location.pathname}`;
    setCurrentPage(fullPage);
  }, [location.pathname]);
  if (!currentPage) return null;
  function extractServiceTypeOnly(title: string) {
    return title
      .replace(/^Jasa Bangun Rumah\s+/, "") // Hapus prefix
      .replace(/\s+Di\s+.*$/, "") // Hapus bagian setelah "Di ..."
      .trim(); // Bersihkan spasi
  }
  const toggleContact = () => {
    const phoneNumber = WHATSAPP_NUM;
    const message = encodeURIComponent(WHATSAPP_MESSAGE);
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappURL, "_blank");
  };
  return (
    <>
      <Navbar />
      <GlobalMetaScripts
        structuredData={structuredData}
        serviceSchema={serviceSchema}
        businessSchema={businessSchema}
        productSchema={productSchema}
      />
      <GlobalBreadcrumb items={breadcrumbItems} />
      <GlobalCompoVideo />
      <section className="w-global mx-auto">
        <div className="pt-[3rem]">
          {/* <HomeSectionHero toggleContact={toggleContact} /> */}
          <section className="h-[100vh]">
            <GlobalSectionHero />
          </section>
          <GlobalSeoDescriptionContent
            slug={districtData.slug}
            district={districtData.district}
            currentPage={currentPage}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <GlobalCardByServices
            services={districtData.services}
            district={districtData.district}
            serviceName="Jasa Bangun Rumah"
            extractServiceTypeOnly={extractServiceTypeOnly}
            currentPage={currentPage}
          />
        </div>
      </section>
      <section className="pb-[10rem] m-global mx-auto relative overflow-hidden no-scrollbar">
        <ExploreJasaByCitySection />
      </section>
      <Footer />
    </>
  );
}
